/**
 * Tahap 7C — Halaman Ekspor Laporan (Excel + PDF) & Report Pack.
 * RBAC-aware · 100% Bahasa Indonesia.
 */
import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { formatDateID } from "@/lib/format";
import { Download, FileSpreadsheet, FileText, History, Package as PkgIcon } from "lucide-react";

const RBAC = {
  cashflow: "FIN_CASHFLOW_LIHAT",
  ar_aging: "FIN_AGING_LIHAT",
  ap_aging: "FIN_AGING_LIHAT",
  financial_integrity: "FIN_INTEGRITY_LIHAT",
  fixed_asset_register: "ASET_LIHAT",
  management_dashboard: "FIN_DASHBOARD_LIHAT",
  report_pack: "FIN_DASHBOARD_LIHAT",
};

const PERMS_BY_ROLE = {
  super_admin: ["*"],
  keuangan: ["FIN_CASHFLOW_LIHAT", "FIN_AGING_LIHAT", "FIN_CASH_POSITION_LIHAT", "FIN_DASHBOARD_LIHAT", "FIN_INTEGRITY_LIHAT", "ASET_LIHAT"],
  akuntansi: ["FIN_CASHFLOW_LIHAT", "FIN_AGING_LIHAT", "FIN_CASH_POSITION_LIHAT", "FIN_DASHBOARD_LIHAT", "FIN_INTEGRITY_LIHAT", "ASET_LIHAT"],
  direktur_utama: ["FIN_CASHFLOW_LIHAT", "FIN_AGING_LIHAT", "FIN_CASH_POSITION_LIHAT", "FIN_DASHBOARD_LIHAT", "FIN_INTEGRITY_LIHAT", "ASET_LIHAT"],
  direktur_operasional: ["FIN_CASHFLOW_LIHAT", "FIN_AGING_LIHAT", "FIN_CASH_POSITION_LIHAT", "FIN_DASHBOARD_LIHAT", "ASET_LIHAT"],
  auditor: ["FIN_CASHFLOW_LIHAT", "FIN_AGING_LIHAT", "FIN_CASH_POSITION_LIHAT", "FIN_DASHBOARD_LIHAT", "FIN_INTEGRITY_LIHAT", "ASET_LIHAT"],
  viewer: ["FIN_DASHBOARD_LIHAT", "ASET_LIHAT"],
  perpajakan: ["FIN_AGING_LIHAT", "FIN_INTEGRITY_LIHAT", "ASET_LIHAT"],
};

function useExportCan() {
  const { user } = useAuth();
  const perms = PERMS_BY_ROLE[user?.role] || [];
  const isSuper = user?.role === "super_admin" || perms.includes("*");
  return (report_key) => isSuper || perms.includes(RBAC[report_key]);
}

async function downloadExport(endpoint, body, filename) {
  try {
    const res = await api.post(`/exports/${endpoint}`, body, { responseType: "blob" });
    const blob = new Blob([res.data]);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; document.body.appendChild(a);
    a.click(); a.remove();
    window.URL.revokeObjectURL(url);
    toast.success(`Berkas ${filename} berhasil diunduh`);
  } catch (e) {
    toast.error(formatApiError(e));
  }
}

function ExportRow({ report_key, endpoint, label, description, extraFields, defaultBody }) {
  const can = useExportCan();
  const [body, setBody] = useState(defaultBody || {});
  if (!can(report_key)) return null;
  const trigger = (fmt) => {
    const today = new Date().toISOString().slice(0, 10);
    const fn = `${endpoint.replace(/\//g, "-")}_${body.year || today}${body.month ? "-" + String(body.month).padStart(2,"0") : ""}.${fmt}`;
    return downloadExport(endpoint, { ...body, format: fmt }, fn);
  };
  return (
    <Card data-testid={`export-card-${report_key}`}><CardContent className="p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-semibold">{label}</div>
          <div className="text-xs text-slate-500">{description}</div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => trigger("xlsx")} data-testid={`export-xlsx-${report_key}`}><FileSpreadsheet className="h-4 w-4 mr-1" />Excel</Button>
          <Button size="sm" onClick={() => trigger("pdf")} data-testid={`export-pdf-${report_key}`}><FileText className="h-4 w-4 mr-1" />PDF</Button>
        </div>
      </div>
      {extraFields && <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
        {extraFields.map(f => (
          <div key={f.key}>
            <Label className="text-xs">{f.label}</Label>
            <Input type={f.type || "text"} value={body[f.key] ?? ""} onChange={e => setBody({ ...body, [f.key]: f.type === "number" ? parseInt(e.target.value) : e.target.value })} data-testid={`export-${report_key}-${f.key}`} />
          </div>
        ))}
      </div>}
    </CardContent></Card>
  );
}

export function ExportsHub() {
  const today = new Date();
  const yr = today.getFullYear();
  const mo = today.getMonth() + 1;
  return (
    <div className="space-y-6 page-fade" data-testid="exports-hub">
      <div>
        <div className="text-xs text-slate-500 mb-2">Keuangan › Ekspor Laporan</div>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Ekspor Laporan (Excel + PDF)</h1>
          <div className="flex gap-2">
            <a href="/ekspor/perusahaan" data-testid="link-company"><Button variant="outline" size="sm">Identitas Perusahaan</Button></a>
            <a href="/ekspor/audit" data-testid="link-audit"><Button variant="outline" size="sm"><History className="h-4 w-4 mr-1" />Audit Trail</Button></a>
          </div>
        </div>
        <p className="text-sm text-slate-500 mt-2">Semua ekspor mengikuti RBAC. Laporan periode <strong>LOCKED</strong> reproducible + hash immutable. Audit trail otomatis dicatat.</p>
      </div>

      <ExportRow report_key="cashflow" endpoint="cashflow"
        label="Laporan Arus Kas (Direct + Indirect)"
        description="Rekonsiliasi terhadap Δ kas — Rp 0"
        defaultBody={{ date_from: `${yr}-01-01`, date_to: `${yr}-12-31` }}
        extraFields={[{ key: "date_from", label: "Dari", type: "date" },
                       { key: "date_to", label: "Sampai", type: "date" },
                       { key: "period", label: "Periode LOCKED (opsional YYYY-MM)" }]} />

      <ExportRow report_key="ar_aging" endpoint="ar-aging"
        label="Aging Piutang (AR)" description="5 bucket · per pelanggan"
        defaultBody={{ as_of: today.toISOString().slice(0, 10) }}
        extraFields={[{ key: "as_of", label: "Per Tanggal", type: "date" },
                       { key: "customer_code", label: "Kode Pelanggan (opsional)" }]} />

      <ExportRow report_key="ap_aging" endpoint="ap-aging"
        label="Aging Hutang (AP)" description="5 bucket · per pemasok"
        defaultBody={{ as_of: today.toISOString().slice(0, 10) }}
        extraFields={[{ key: "as_of", label: "Per Tanggal", type: "date" },
                       { key: "supplier_code", label: "Kode Pemasok (opsional)" }]} />

      <ExportRow report_key="financial_integrity" endpoint="financial-integrity"
        label="Pusat Integritas Keuangan (12 Cek)"
        description="Semua invariant Rp 0" defaultBody={{}} />

      <ExportRow report_key="fixed_asset_register" endpoint="fixed-asset-register"
        label="Register Aset Tetap" description="Semua status atau filter"
        defaultBody={{}}
        extraFields={[{ key: "status", label: "Status (opsional: ACTIVE/DRAFT/SOLD/DISPOSED)" }]} />

      <ExportRow report_key="management_dashboard" endpoint="management-dashboard"
        label="Dasbor Manajemen Bulanan"
        description="15 metrics operating & keuangan"
        defaultBody={{ year: yr, month: mo }}
        extraFields={[{ key: "year", label: "Tahun", type: "number" },
                       { key: "month", label: "Bulan (1-12)", type: "number" }]} />

      <Card className="border-2 border-emerald-500" data-testid="report-pack-card">
        <CardContent className="p-4">
          <div className="flex items-start gap-3 mb-3">
            <PkgIcon className="h-8 w-8 text-emerald-600 mt-1" />
            <div>
              <div className="font-semibold text-lg">Paket Laporan Manajemen Bulanan</div>
              <div className="text-xs text-slate-500">1 file: Neraca + Laba Rugi + Arus Kas + AR/AP Aging + Persediaan/HPP + Fixed Asset + Pajak + Integritas</div>
            </div>
          </div>
          <ReportPackForm />
        </CardContent>
      </Card>
    </div>
  );
}

function ReportPackForm() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const trigger = (fmt) => downloadExport("report-pack", { format: fmt, year, month },
                                             `paket-laporan_${year}-${String(month).padStart(2,"0")}.${fmt}`);
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 items-end">
      <div><Label>Tahun</Label><Input type="number" value={year} onChange={e => setYear(parseInt(e.target.value) || year)} data-testid="pack-year" /></div>
      <div><Label>Bulan</Label><Input type="number" min={1} max={12} value={month} onChange={e => setMonth(parseInt(e.target.value) || month)} data-testid="pack-month" /></div>
      <Button variant="outline" onClick={() => trigger("xlsx")} data-testid="pack-xlsx"><FileSpreadsheet className="h-4 w-4 mr-1" />Excel</Button>
      <Button onClick={() => trigger("pdf")} data-testid="pack-pdf" className="bg-emerald-600"><FileText className="h-4 w-4 mr-1" />PDF</Button>
    </div>
  );
}

export function CompanyProfilePage() {
  const { user } = useAuth();
  const canEdit = ["super_admin", "akuntansi", "direktur_utama"].includes(user?.role);
  const [c, setC] = useState({ name: "", address: "", npwp: "", phone: "", email: "", logo_url: "" });
  const [loaded, setLoaded] = useState(false);
  const load = async () => {
    try { const { data } = await api.get("/exports/company"); setC({ ...c, ...data }); setLoaded(true); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      await api.put("/exports/company", {
        name: c.name, address: c.address || null, npwp: c.npwp || null,
        phone: c.phone || null, email: c.email || null,
        logo_url: c.logo_url || null,
      });
      toast.success("Identitas perusahaan disimpan");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-6 page-fade" data-testid="company-profile-page">
      <div>
        <div className="text-xs text-slate-500 mb-2">Keuangan › Ekspor › Identitas Perusahaan</div>
        <h1 className="font-display text-3xl font-bold">Identitas Perusahaan (Master Company)</h1>
        <p className="text-sm text-slate-500 mt-2">Data ini digunakan di semua header laporan Excel & PDF (individual + Report Pack).</p>
      </div>
      {!canEdit && <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-amber-900 text-sm" data-testid="company-readonly-banner">Anda dapat melihat identitas perusahaan tetapi tidak dapat mengeditnya. Hanya <strong>super_admin</strong>, <strong>akuntansi</strong>, dan <strong>direktur_utama</strong> yang berhak mengedit.</div>}
      {loaded && (
        <Card><CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label>Nama Perusahaan *</Label><Input value={c.name || ""} onChange={e => setC({...c, name: e.target.value})} data-testid="company-name" disabled={!canEdit} /></div>
          <div><Label>NPWP</Label><Input value={c.npwp || ""} onChange={e => setC({...c, npwp: e.target.value})} data-testid="company-npwp" disabled={!canEdit} /></div>
          <div className="md:col-span-2"><Label>Alamat</Label><Input value={c.address || ""} onChange={e => setC({...c, address: e.target.value})} data-testid="company-address" disabled={!canEdit} /></div>
          <div><Label>Telepon</Label><Input value={c.phone || ""} onChange={e => setC({...c, phone: e.target.value})} data-testid="company-phone" disabled={!canEdit} /></div>
          <div><Label>Email</Label><Input value={c.email || ""} onChange={e => setC({...c, email: e.target.value})} data-testid="company-email" disabled={!canEdit} /></div>
          <div className="md:col-span-2"><Label>URL Logo (opsional)</Label><Input value={c.logo_url || ""} onChange={e => setC({...c, logo_url: e.target.value})} data-testid="company-logo" disabled={!canEdit} /></div>
          {canEdit && <div className="md:col-span-2"><Button onClick={save} data-testid="company-save">Simpan Identitas</Button></div>}
        </CardContent></Card>
      )}
    </div>
  );
}


export function ExportAuditLogPage() {
  const [logs, setLogs] = useState([]);
  const load = async () => {
    try { const { data } = await api.get("/exports/audit-log?limit=200"); setLogs(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-6 page-fade" data-testid="export-audit-log-page">
      <div>
        <div className="text-xs text-slate-500 mb-2">Keuangan › Ekspor › Audit Trail</div>
        <div className="flex items-center gap-3">
          <History className="h-6 w-6 text-slate-700" />
          <h1 className="font-display text-3xl font-bold">Audit Trail Ekspor Laporan</h1>
        </div>
      </div>
      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow><TableHead>Waktu</TableHead><TableHead>User</TableHead><TableHead>Peran</TableHead><TableHead>Laporan</TableHead><TableHead>Format</TableHead><TableHead>Parameter</TableHead><TableHead>Ukuran</TableHead><TableHead>Reproducible</TableHead><TableHead>Hash</TableHead></TableRow></TableHeader>
          <TableBody>
            {logs.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-6 text-slate-400">Belum ada entri</TableCell></TableRow>}
            {logs.map(l => (
              <TableRow key={l.id} data-testid={`log-row-${l.id}`}>
                <TableCell className="text-xs">{formatDateID(l.generated_at)}</TableCell>
                <TableCell>{l.user_name}</TableCell>
                <TableCell><Badge variant="outline">{l.user_role}</Badge></TableCell>
                <TableCell>{l.report_key}</TableCell>
                <TableCell><Badge className={l.format === "xlsx" ? "bg-emerald-600" : "bg-rose-600"}>{l.format.toUpperCase()}</Badge></TableCell>
                <TableCell className="text-xs font-mono-num">{JSON.stringify(l.params).slice(0, 60)}</TableCell>
                <TableCell className="font-mono-num text-xs">{Math.round((l.size_bytes || 0) / 1024)} KB</TableCell>
                <TableCell>{l.reproducible ? <Badge className="bg-emerald-600">LOCKED</Badge> : <Badge variant="outline">-</Badge>}</TableCell>
                <TableCell className="font-mono-num text-xs">{l.hash ? l.hash.slice(0, 12) + "…" : "-"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}
